/**
 * Orchestration Components Styles
 */

import styled, { css } from 'styled-components';

// Status Badge
export const StatusBadge = styled.div<{ color: string; size?: 'small' | 'medium' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ size }) => (size === 'small' ? '4px 12px' : '6px 16px')};
  border-radius: 16px;
  background-color: ${({ color }) => `${color}20`};
  border: 1px solid ${({ color }) => color};
  color: ${({ color }) => color};
  font-size: ${({ size }) => (size === 'small' ? '12px' : '14px')};
  font-weight: 600;
  white-space: nowrap;
`;

// Filters Bar
export const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

export const SearchBox = styled.div`
  flex: 1;
  min-width: 250px;
`;

// Request Card
export const RequestCard = styled.div`
  background: ${({ theme }) => theme.background?.secondary || 'rgba(255, 255, 255, 0.05)'};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid ${({ theme }) => theme.text?.secondary || 'rgba(255, 255, 255, 0.1)'};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.primary || '#FFC700'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

export const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CardActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const CardBody = styled.div`
  margin-bottom: 16px;
`;

export const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.text?.secondary || 'rgba(255, 255, 255, 0.1)'};
`;

export const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

export const InfoLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.text?.primary || '#fff'};
`;

export const InfoValue = styled.span`
  color: ${({ theme }) => theme.text?.secondary || '#bbb'};
`;

// Request List
export const RequestsListContainer = styled.div`
  margin-top: 24px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
`;

export const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

// Modal Styles
export const ModalContent = styled.div`
  padding: 24px;
`;

export const ModalSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ModalSectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.text?.primary || '#fff'};
`;

export const FormDataDisplay = styled.div`
  background: ${({ theme }) => theme.background?.primary || 'rgba(0, 0, 0, 0.2)'};
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 12px;
`;

export const FormDataRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const FormDataLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.text?.secondary || '#bbb'};
  margin-bottom: 4px;
  text-transform: uppercase;
`;

export const FormDataValue = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text?.primary || '#fff'};
  word-break: break-word;
`;

export const LinkDisplayBox = styled.div`
  background: ${({ theme }) => theme.background?.primary || 'rgba(0, 0, 0, 0.2)'};
  padding: 16px;
  border-radius: 8px;
  border: 2px dashed ${({ theme }) => theme.primary || '#FFC700'};
  margin-bottom: 16px;
`;

export const LinkText = styled.div`
  font-family: monospace;
  font-size: 14px;
  color: ${({ theme }) => theme.primary || '#FFC700'};
  word-break: break-all;
  margin-bottom: 12px;
`;

export const CopyButton = styled.button`
  background: ${({ theme }) => theme.primary || '#FFC700'};
  color: #000;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    transform: scale(0.98);
  }
`;

// Stats Bar
export const StatsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

export const StatCard = styled.div<{ active?: boolean }>`
  flex: 1;
  min-width: 150px;
  padding: 16px;
  background: ${({ theme }) => theme.background?.secondary || 'rgba(255, 255, 255, 0.05)'};
  border-radius: 8px;
  border: 2px solid
    ${({ active, theme }) =>
      active ? theme.primary || '#FFC700' : theme.text?.secondary || 'rgba(255, 255, 255, 0.1)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary || '#FFC700'};
    transform: translateY(-2px);
  }
`;

export const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.primary || '#FFC700'};
  margin-bottom: 4px;
`;

export const StatLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text?.secondary || '#bbb'};
`;

// Sort and View Controls
export const SortControl = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const FilterChip = styled.div<{ color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 16px;
  background-color: ${({ color, theme }) =>
    color ? `${color}20` : theme.background?.secondary || 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${({ color, theme }) => color || theme.text?.secondary || 'rgba(255, 255, 255, 0.1)'};
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.text?.primary || '#fff'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ color, theme }) =>
      color ? `${color}30` : 'rgba(255, 255, 255, 0.1)'};
  }
`;

export const FilterChipsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

// Empty State with Illustration
export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
  background: ${({ theme }) => theme.background?.secondary || 'rgba(255, 255, 255, 0.02)'};
  border-radius: 12px;
  border: 2px dashed ${({ theme }) => theme.text?.secondary || 'rgba(255, 255, 255, 0.1)'};
`;

export const EmptyStateIllustration = styled.div`
  font-size: 80px;
  margin-bottom: 24px;
  opacity: 0.6;
  filter: grayscale(0.3);
`;

// Loading Skeleton
export const LoadingSkeletonCard = styled.div`
  background: ${({ theme }) => theme.background?.secondary || 'rgba(255, 255, 255, 0.05)'};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid ${({ theme }) => theme.text?.secondary || 'rgba(255, 255, 255, 0.1)'};
`;

export const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Requests Grid (supports both grid and list view)
export const RequestsGrid = styled.div<{ viewMode?: 'grid' | 'list' }>`
  display: ${({ viewMode }) => (viewMode === 'list' ? 'flex' : 'grid')};
  flex-direction: ${({ viewMode }) => (viewMode === 'list' ? 'column' : 'unset')};
  grid-template-columns: ${({ viewMode }) =>
    viewMode === 'list' ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))'};
  gap: 16px;
  margin-top: 24px;
`;

// Pagination Container
export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.text?.secondary || 'rgba(255, 255, 255, 0.1)'};
`;

export const PaginationInfo = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text?.secondary || '#bbb'};
  font-weight: 500;
`;

// UserDisplay Components
export const UserDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const Avatar = styled.div<{ size?: 'small' | 'medium' | 'large'; src?: string }>`
  width: ${({ size }) => (size === 'small' ? '32px' : size === 'medium' ? '40px' : '48px')};
  height: ${({ size }) => (size === 'small' ? '32px' : size === 'medium' ? '40px' : '48px')};
  border-radius: 50%;
  background: ${({ src, theme }) =>
    src ? `url(${src}) center/cover` : theme.primary || '#FFC700'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${({ size }) => (size === 'small' ? '12px' : size === 'medium' ? '14px' : '16px')};
  color: #000;
  flex-shrink: 0;
`;

export const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const UserName = styled.div<{ size?: 'small' | 'medium' | 'large' }>`
  font-size: ${({ size }) => (size === 'small' ? '13px' : size === 'medium' ? '14px' : '16px')};
  font-weight: 600;
  color: ${({ theme }) => theme.text?.primary || '#fff'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UserEmail = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text?.secondary || '#bbb'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
